import { isNumber } from 'lodash'

// Temporary workaround. Resolve ASAP. Do not add more ignores like this.
// eslint-disable-next-line eslint-comments/no-restricted-disable
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BuyButtonPosition } from '@marketplace-web/messaging/conversation-messaging-data'
import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { Screen } from 'constants/tracking/screens'

export enum ReasonForNotBundling {
  ShippingSize = 'shipping_size_mismatch',
}

type CannotBundleButtonViewEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleButtonViewEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

export const cannotBundleButtonViewEvent = (
  args: CannotBundleButtonViewEventArgs,
): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleButtonViewEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_button_view',
    extra,
  }
}

type CannotBundleButtonClickEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleButtonClickEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

export const cannotBundleButtonClickEvent = (
  args: CannotBundleButtonClickEventArgs,
): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleButtonClickEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_button_click',
    extra,
  }
}

type CannotBundleHyperlinkClickEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleHyperlinkClickEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

export const cannotBundleHyperlinkClickEvent = (
  args: CannotBundleHyperlinkClickEventArgs,
): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleHyperlinkClickEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_hyperlink_click',
    extra,
  }
}

type ClickBundleDiscountSuggestionEventExtra = {
  discounts_enabled: boolean
  screen: Screen
}

type ClickBundleDiscountSuggestionEventArgs = {
  discountsEnabled: boolean
  screen: Screen
}

export const clickBundleDiscountSuggestionEvent = (
  args: ClickBundleDiscountSuggestionEventArgs,
): TrackingEvent => {
  const { discountsEnabled, screen } = args

  const extra: ClickBundleDiscountSuggestionEventExtra = {
    discounts_enabled: discountsEnabled,
    screen,
  }

  return {
    event: 'user.bundle_discount_settings_click',
    extra,
  }
}

type ClickBundleDiscountEnableEventExtra = {
  enable_discount: boolean
  screen: Screen
}

type ClickBundleDiscountEnableEventArgs = {
  enableDiscount: boolean
  screen: Screen
}

export const clickBundleDiscountEnableEvent = (
  args: ClickBundleDiscountEnableEventArgs,
): TrackingEvent => {
  const { enableDiscount, screen } = args

  const extra: ClickBundleDiscountEnableEventExtra = {
    enable_discount: enableDiscount,
    screen,
  }

  return {
    event: 'user.bundle_discount_enable',
    extra,
  }
}

type CloseBundleDiscountScreenEventExtra = {
  discount_2_items: number | null
  discount_3_items: number | null
  discount_5_items: number | null
  screen: Screen
}

type CloseBundleDiscountScreenEventArgs = {
  discount2Items: number | null
  discount3Items: number | null
  discount5Items: number | null
  screen: Screen
}

export const closeBundleDiscountScreenEvent = (
  args: CloseBundleDiscountScreenEventArgs,
): TrackingEvent => {
  const { discount2Items, discount3Items, discount5Items, screen } = args

  const extra: CloseBundleDiscountScreenEventExtra = {
    discount_2_items: discount2Items,
    discount_3_items: discount3Items,
    discount_5_items: discount5Items,
    screen,
  }

  return {
    event: 'user.bundle_discount_amount',
    extra,
  }
}

type ClickEventArgs = {
  target: ClickableElement
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: ClickableElement
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

type ClickListItemEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchScore?: string
  searchSignals?: Array<string> | null
  itemDistance?: number | null
  homepageSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ClickListEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_score?: string
  search_signals?: Array<string>
  buyer_item_distance_km?: number
  homepage_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const clickListItemEvent = (args: ClickListItemEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId,
    screen,
    metadata,
  } = args

  const extra: ClickListEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (searchScore) extra.search_score = searchScore
  if (searchSignals) extra.search_signals = searchSignals
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'user.click_list_item',
    extra,
  }
}

type WantItemButtonExtra =
  | 'message'
  | 'buy'
  | 'item_reservation_request'
  | 'make_offer'
  | 'create_bundle'

type BuyerWantItemEventExtra = {
  item_id: number | null
  transaction_id: number | null
  button: WantItemButtonExtra
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id: string | null
  homepage_session_id?: string | null
}

type BuyerWantItemEventArgs = {
  transactionId: number | null
  itemId: number | null
  button: WantItemButtonExtra
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId: string | null
  homepageSessionId?: string | null
}

export const buyerWantItemEvent = ({
  transactionId,
  itemId,
  button,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  searchSessionId,
  homepageSessionId,
}: BuyerWantItemEventArgs): TrackingEvent => {
  const extra: BuyerWantItemEventExtra = {
    button,
    item_id: itemId,
    transaction_id: transactionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    search_session_id: searchSessionId,
    homepage_session_id: homepageSessionId,
  }

  return {
    event: 'buyer.want_item',
    extra,
  }
}

type BuyerBuyEventArgs = {
  transactionId: number
  screen: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string | null
  sellerOfferId?: number
  buttonPosition?: BuyButtonPosition
}

type BuyerBuyEventExtra = {
  transaction_id: number
  screen: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string | null
  seller_offer_id?: number
  button_position?: BuyButtonPosition
}

export const buyerBuyEvent = (args: BuyerBuyEventArgs) => {
  const {
    transactionId,
    screen,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    sellerOfferId,
    buttonPosition,
  } = args

  const extra: BuyerBuyEventExtra = {
    transaction_id: transactionId,
    screen,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId,
    homepage_session_id: homepageSessionId,
    seller_offer_id: sellerOfferId,
    button_position: buttonPosition,
  }

  return {
    event: 'buyer.buy',
    extra,
  }
}

type ItemPageViewEventExtra = {
  target: string
  item_id: string
  details?: string
  item_page_session_id?: string
}

export const itemPageViewEvent = (args: ItemPageViewEventExtra) => {
  const { target, item_id, details, item_page_session_id } = args

  const extra: ItemPageViewEventExtra = {
    target,
    item_id,
  }

  if (details) extra.details = details
  if (item_page_session_id) extra.item_page_session_id = item_page_session_id

  return {
    event: 'item_page.view',
    extra,
  }
}
